import { Button } from '@nextui-org/react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUserContext } from '~/contexts/UserContext'
import {
  acceptBillingInvitation,
  getBillingInvitationInfo,
  InvitationInfo
} from '~/services/superAdminService'
import { BetterToast } from '~/utils'

function LoadingSpinner() {
  return (
    <div className='flex items-center justify-center h-screen'>
      <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500'></div>
    </div>
  )
}

function AcceptBillingInvitePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, loaded, user } = useUserContext()
  const userEmail = user?.email

  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isAccepted, setIsAccepted] = useState(false)
  const [acceptedAccountName, setAcceptedAccountName] = useState<string | null>(null)

  const token = searchParams.get('token')

  // Fetch invitation info
  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link')
      return
    }

    getBillingInvitationInfo(token)
      .then(info => {
        setInvitationInfo(info)
        if (!info.valid) {
          setError(info.error || 'Invalid or expired invitation')
        }
      })
      .catch(err => {
        console.error('Error fetching invitation info:', err)
        setError('Failed to load invitation details')
      })
  }, [token])

  // Handle authentication and acceptance flow
  useEffect(() => {
    // Wait for auth state and invitation info to load
    if (!loaded || !invitationInfo || !invitationInfo.valid) return

    // If not authenticated, redirect to signin or signup
    if (!isAuthenticated) {
      if (invitationInfo.user_exists) {
        // User exists, redirect to signin with callback to this page
        const signinMsg = 'Please sign in to accept the billing account invitation'
        navigate(
          `/signin?msg=${encodeURIComponent(signinMsg)}&redirect=${encodeURIComponent(`/accept-billing-invite?token=${token}`)}`,
          { replace: true }
        )
      } else {
        // User doesn't exist, redirect to signup with token
        // SignUpForm will fetch invitation info and handle the flow
        navigate(`/signup?token=${token}`, { replace: true })
      }
      return
    }

    // User is authenticated
    // Check if email matches
    if (
      userEmail &&
      invitationInfo.email &&
      userEmail.toLowerCase() !== invitationInfo.email.toLowerCase()
    ) {
      setError(
        `This invitation was sent to ${invitationInfo.email}. You are signed in as ${userEmail}. Please sign out and sign in with the correct account.`
      )
      return
    }

    // Accept the invitation if not already processing (for existing users who signed in)
    if (!isProcessing && !isAccepted) {
      setIsProcessing(true)

      acceptBillingInvitation(token!)
        .then(result => {
          if (result.success) {
            setIsAccepted(true)
            setAcceptedAccountName(result.account_name || null)
            BetterToast.success(result.message)
          } else {
            setError(result.message)
          }
        })
        .catch(err => {
          console.error('Error accepting invitation:', err)
          const errorMessage = err instanceof Error ? err.message : 'Failed to accept invitation'
          setError(errorMessage)
        })
        .finally(() => {
          setIsProcessing(false)
        })
    }
  }, [
    loaded,
    isAuthenticated,
    invitationInfo,
    token,
    userEmail,
    isProcessing,
    isAccepted,
    navigate
  ])

  // Show loading state
  if (!loaded || (token && !invitationInfo && !error)) {
    return <LoadingSpinner />
  }

  // Show processing state
  if (isProcessing) {
    return (
      <div className='flex flex-col items-center justify-center h-screen gap-4'>
        <LoadingSpinner />
        <p className='text-base text-gray-600'>Accepting invitation...</p>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className='flex flex-col items-center justify-center h-screen gap-6 px-4'>
        <div className='text-center max-w-md'>
          <div className='mb-4'>
            <svg
              className='mx-auto h-12 w-12 text-red-400'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          </div>
          <h2 className='text-2xl font-semibold mb-2'>Invitation Error</h2>
          <p className='text-base text-gray-600 mb-6'>{error}</p>
          <Button color='primary' onClick={() => navigate('/')}>
            Go to Home
          </Button>
        </div>
      </div>
    )
  }

  // Show success state
  if (isAccepted) {
    return (
      <div className='flex flex-col items-center justify-center h-screen gap-6 px-4'>
        <div className='text-center max-w-md'>
          <div className='mb-4'>
            <svg
              className='mx-auto h-12 w-12 text-green-400'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          <h2 className='text-2xl font-semibold mb-2'>Welcome!</h2>
          <p className='text-base text-gray-600 mb-6'>
            You&apos;ve been added to{' '}
            {acceptedAccountName ? <strong>{acceptedAccountName}</strong> : 'the billing account'}.
          </p>
          <Button color='primary' onClick={() => navigate('/')}>
            Get Started
          </Button>
        </div>
      </div>
    )
  }

  // Show invitation details (waiting for action)
  if (invitationInfo && invitationInfo.valid) {
    return (
      <div className='flex flex-col items-center justify-center h-screen gap-6 px-4'>
        <div className='text-center max-w-md'>
          <h2 className='text-2xl font-semibold mb-2'>Billing Account Invitation</h2>
          <p className='text-base text-gray-600 mb-4'>
            You&apos;ve been invited to join <strong>{invitationInfo.account_name}</strong>
          </p>
          {invitationInfo.inviter_email && (
            <p className='text-sm text-gray-500 mb-6'>Invited by {invitationInfo.inviter_email}</p>
          )}
          <div className='animate-pulse'>
            <p className='text-sm text-gray-400'>Processing...</p>
          </div>
        </div>
      </div>
    )
  }

  // Fallback loading
  return <LoadingSpinner />
}

export default AcceptBillingInvitePage
